import React from "react";
import Header from "./components/Header";
import YogurtBuilder from "./components/YogurtBuilder";
import CartSidebar from "./components/CartSidebar";
import CheckoutDialog from "./components/CheckoutDialog";
import Footer from "./components/Footer";
import AboutPage from "./components/AboutPage";
import { useYogurtBuilder } from "./hooks/useYogurtBuilder";
import { useCart } from "./hooks/useCart";
import { useWhatsAppCheckout } from "./hooks/useWhatsAppCheckout";
import { useCatalog } from "./context/CatalogContext.jsx";

/**
 * Custom Miyogur – Frontend (React + Tailwind)
 * Pure Tailwind version with modular components and custom hooks.
 */
export default function MiyogurApp() {
  const { health, loading, error, refresh } = useCatalog();
  // Custom hooks for state management
  const builder = useYogurtBuilder();
  const { cart, addToCart, removeFromCart, clearCart } = useCart();
  const { openCheckout, submitting, setOpenCheckout, processCheckout } = useWhatsAppCheckout();
  
  // Navigation state
  const [currentPage, setCurrentPage] = React.useState("home");

  // Handle adding product to cart
  const handleAddToCart = () => {
    if (!builder.isValid()) return;

    const product = builder.getCurrentProduct();
    addToCart(product);
    builder.resetBuilder();
  };

  // Handle checkout process
  const handleProcessCheckout = () => {
    processCheckout(cart, clearCart);
  };

  // Calculate total cart items for header
  const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);

  // Show About page if currentPage is "about"
  if (currentPage === "about") {
    return <AboutPage onBack={() => setCurrentPage("home")} />;
  }

  return (
    <div className="min-h-screen bg-white">
      {error && (
        <div className="bg-rose-50 border-b border-rose-200 text-rose-700 text-sm text-center px-4 py-2">
          Problema al sincronizar con el backend. Usando datos locales.
          <button
            type="button"
            onClick={() => refresh()}
            className="ml-2 underline"
          >
            Reintentar
          </button>
        </div>
      )}
      {/* Header */}
      <Header 
        cartItemCount={cartItemCount}
        onCartClick={() => setOpenCheckout(true)}
        backendStatus={{ loading, health, error }}
        onRetry={refresh}
      />

      {/* Main content */}
      <main className="mx-auto max-w-6xl px-3 py-5 grid gap-5 md:grid-cols-5">
        {/* yogur Builder */}
        <YogurtBuilder 
          builderState={builder}
          builderActions={builder}
          onAddToCart={handleAddToCart}
          onAboutClick={() => setCurrentPage("about")}
        />

        {/* Cart Sidebar */}
        <CartSidebar 
          cart={cart}
          onRemoveItem={removeFromCart}
          onCheckout={() => setOpenCheckout(true)}
        />
      </main>

      {/* Checkout Dialog */}
      <CheckoutDialog 
        open={openCheckout}
        onOpenChange={setOpenCheckout}
        cart={cart}
        onProcessCheckout={handleProcessCheckout}
        submitting={submitting}
      />

      {/* Footer */}
      <Footer />
    </div>
  );
}
